// sortcols.js — ONE place the "click a column header to sort asc/desc" behaviour lives, so the
// reports tables and the cashbook / journal registers all behave identically (owner 2026-08-26:
// "sortable by clicking on the column headers asc/desc"). Stateless by design: the caller owns the
// current { key, dir } (a signal or plain var) and re-renders after onSort mutates it.
import { html } from './t4.js';

// A sortable <th>. `cur` is the current { key, dir }; clicking (or Enter/Space) fires onSort(key).
// Shows ▲/▼ on the active column only. opts.align:'right' right-aligns numeric columns.
// IMPORTANT (tina4js): read the current sort value at the TOP of the owning reactive block and pass
// it in here as `cur` — never let this run inside a nested `${() => …}` slot (those APPEND on re-render).
export function sortTh(label, key, cur, onSort, opts = {}) {
  const active = !!cur && cur.key === key;
  const ind = active ? (cur.dir === 'asc' ? ' ▲' : ' ▼') : '';
  const cls = 'sort-th' + (opts.align === 'right' ? ' text-right' : '') + (active ? ' sort-active' : '');
  return html`<th class=${cls} role="button" tabindex="0"
    aria-sort=${active ? (cur.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    title=${'Sort by ' + label}
    @click=${() => onSort(key)}
    @keydown=${e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSort(key); } }}
    >${label}<span class="sort-ind">${ind}</span></th>`;
}

// Toggle helper: clicking the ACTIVE column flips its direction; clicking a NEW column starts it at
// `defaultDir` (text reads best ascending, dates/amounts descending — the caller decides per key).
export function nextSort(cur, key, defaultDir = 'asc') {
  if (cur && cur.key === key) return { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' };
  return { key, dir: defaultDir };
}

// Generic client-side comparator for a FULLY-LOADED table (e.g. the reports List of Accounts — the
// whole dataset is in memory, so sorting the array is correct). `get(row, key)` returns the value to
// compare: strings compare case-insensitively, numbers numerically, null/undefined always sort last.
// Returns a new array; never mutates the input. (Server-paginated registers must NOT use this — they
// sort server-side so the order holds across pages.)
export function sortRows(rows, cur, get) {
  if (!cur || !cur.key) return rows;
  const s = cur.dir === 'asc' ? 1 : -1;
  const val = r => {
    const v = get(r, cur.key);
    return v == null ? null : (typeof v === 'number' ? v : String(v).toLowerCase());
  };
  return rows.slice().sort((a, b) => {
    const av = val(a), bv = val(b);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;            // nulls last, regardless of direction
    if (bv == null) return -1;
    if (av < bv) return -s;
    if (av > bv) return s;
    return 0;
  });
}
